import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  CalendarDays,
  FileText,
  X,
  CheckCircle2,
  Clock,
  Bike,
  HeartHandshake,
  Landmark,
  type LucideIcon,
} from "lucide-react";
import { fmtDate, fmtDateOnly } from "@/lib/format-date";
import { listBookings, type Booking } from "@/lib/bookings-api";
import { listBookingsForAdmin } from "@/lib/yatra-db";

type B = Booking;

const BADGE: Record<string, string> = {
  confirmed: "bg-primary/12 text-primary border-primary/25",
  pending: "bg-primary/8 text-primary border-primary/20",
  completed: "bg-secondary text-foreground border-border",
  cancelled: "bg-destructive/10 text-destructive border-destructive/25",
};
const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

/**
 * Per-type badge styling. Types are separated by fill weight (flat / tinted / solid) rather than
 * by border or hue alone — the palette has too few hues for that to read at a glance. The icon
 * carries the distinction too, so it never rests on colour alone.
 */
const TYPE_BADGE: Record<string, { cls: string; Icon: LucideIcon }> = {
  rickshaw: {
    cls: "border-border bg-muted text-muted-foreground",
    Icon: Bike,
  },
  volunteer: {
    cls: "border-primary/30 bg-primary/15 text-primary",
    Icon: HeartHandshake,
  },
  visit: {
    cls: "border-transparent bg-secondary-foreground text-secondary",
    Icon: Landmark,
  },
};

function TypeBadge({ type }: { type: string }) {
  const { cls, Icon } = TYPE_BADGE[type.toLowerCase()] ?? {
    cls: "border-dashed border-border bg-transparent text-muted-foreground",
    Icon: Calendar,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${cls}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {type || "—"}
    </span>
  );
}

export const Route = createFileRoute("/admin/bookings")({ component: Page });

function Page() {
  const [filter, setFilter] = useState<"all" | "today" | "upcoming" | "group" | "pending">("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [noteView, setNoteView] = useState<B | null>(null);

  const {
    data: apiList = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-bookings"],
    queryFn: listBookings,
  });

  // Merge backend bookings with locally-submitted ones (e.g. Visit Form) so
  // devotee submissions surface here immediately. Backend is authoritative on id.
  const list = useMemo(() => {
    const local = listBookingsForAdmin();
    const seen = new Set(apiList.map((b) => b.id));
    const merged = [...apiList, ...local.filter((b) => !seen.has(b.id))];
    return merged.sort((a, b) => b.booking_date.localeCompare(a.booking_date));
  }, [apiList]);

  const today = new Date().toISOString().slice(0, 10);
  const stats = useMemo(() => {
    const todayList = list.filter((b) => b.booking_date === today);
    const upcoming = list.filter((b) => b.booking_date > today);
    const group = list.filter((b) => (b.group_size || 1) > 4);
    const pending = list.filter((b) => b.status === "pending");
    const confirmed = list.filter((b) => b.status === "confirmed");
    return { todayList, upcoming, group, pending, confirmed };
  }, [list, today]);

  const types = useMemo(
    () => Array.from(new Set(list.map((b) => b.type).filter(Boolean))).sort(),
    [list],
  );

  const filtered = useMemo(() => {
    let rows: B[];
    switch (filter) {
      case "today":
        rows = stats.todayList;
        break;
      case "upcoming":
        rows = stats.upcoming;
        break;
      case "group":
        rows = stats.group;
        break;
      case "pending":
        rows = stats.pending;
        break;
      default:
        rows = list;
    }
    if (dateFrom) {
      rows = rows.filter((b) => b.booking_date >= dateFrom);
    }
    if (dateTo) {
      rows = rows.filter((b) => b.booking_date <= dateTo);
    }
    if (typeFilter !== "all") {
      rows = rows.filter((b) => b.type === typeFilter);
    }
    return rows;
  }, [filter, list, stats, dateFrom, dateTo, typeFilter]);

  const setStatus = async (_id: string, _status: string) => {
    // TODO: bind Booking status-update API (endpoint not provided yet)
    toast("Booking status API not connected yet");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-serif text-2xl font-bold">Bookings</h2>
          <p className="text-sm text-muted-foreground">
            {list.length} total bookings · {stats.confirmed.length} confirmed
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Today's Bookings"
          value={stats.todayList.length}
          icon={Calendar}
          tone="saffron"
          active={filter === "today"}
          onClick={() => setFilter(filter === "today" ? "all" : "today")}
        />
        <StatCard
          label="Upcoming"
          value={stats.upcoming.length}
          icon={CalendarDays}
          tone="cream"
          active={filter === "upcoming"}
          onClick={() => setFilter(filter === "upcoming" ? "all" : "upcoming")}
        />
        <StatCard
          label="Group Bookings"
          value={stats.group.length}
          icon={Users}
          tone="muted"
          active={filter === "group"}
          onClick={() => setFilter(filter === "group" ? "all" : "group")}
        />
        <StatCard
          label="Pending"
          value={stats.pending.length}
          icon={Clock}
          tone="gold"
          active={filter === "pending"}
          onClick={() => setFilter(filter === "pending" ? "all" : "pending")}
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        {(["all", "today", "upcoming", "group", "pending"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize transition ${filter === f ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}
          >
            {f === "all" ? "All" : f}
          </button>
        ))}

        <div className="mx-1 h-5 w-px bg-border" />

        {/* Date range filter */}
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5">
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-transparent px-1 py-0.5 text-xs font-semibold text-muted-foreground focus:outline-none"
          />
          <span className="text-xs text-muted-foreground">→</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-transparent px-1 py-0.5 text-xs font-semibold text-muted-foreground focus:outline-none"
          />
        </div>

        {/* Booking type filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold capitalize text-muted-foreground focus:border-primary focus:outline-none"
        >
          <option value="all">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        {(dateFrom || dateTo || typeFilter !== "all") && (
          <button
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setTypeFilter("all");
            }}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-muted-foreground transition hover:border-primary/40"
          >
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Member</th>
                <th className="p-3">Type</th>
                <th className="p-3">Provider</th>
                <th className="p-3">Date · Time</th>
                <th className="p-3">Group</th>
                <th className="p-3">Notes</th>
                <th className="p-3">Confirmed</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                    Loading bookings…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load bookings"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                    No bookings match this filter
                  </td>
                </tr>
              )}
              {filtered.map((b) => {
                const isGroup = (b.group_size || 1) > 4;
                return (
                  <tr key={b.id} className="border-t border-border hover:bg-muted/40">
                    <td className="p-3">
                      <div className="font-semibold">{b.member_name || "Devotee"}</div>
                      {b.member_phone && (
                        <a
                          href={`tel:${b.member_phone}`}
                          className="text-[11px] font-medium text-primary hover:underline"
                        >
                          📞 {b.member_phone}
                        </a>
                      )}
                    </td>
                    <td className="p-3">
                      <TypeBadge type={b.type} />
                    </td>
                    <td className="p-3 text-muted-foreground">{b.provider_name || "—"}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      <div className="font-semibold text-foreground">
                        {fmtDateOnly(b.booking_date)}
                      </div>
                      <div className="text-[11px]">{b.booking_time?.slice(0, 5)}</div>
                    </td>
                    <td className="p-3">
                      {isGroup ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                          <Users className="h-3 w-3" />
                          {b.group_size}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{b.group_size || 1}</span>
                      )}
                    </td>
                    <td className="p-3 text-xs">
                      {b.notes ? (
                        <button
                          onClick={() => setNoteView(b)}
                          className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-lg bg-muted px-2 py-1 text-left text-muted-foreground hover:bg-accent"
                        >
                          <FileText className="h-3 w-3 shrink-0" />
                          <span className="truncate">{b.notes}</span>
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-[11px] text-muted-foreground whitespace-nowrap">
                      {b.confirmed_at ? (
                        <span className="inline-flex items-center gap-1 text-primary">
                          <CheckCircle2 className="h-3 w-3" />
                          {fmtDate(b.confirmed_at)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-3">
                      <select
                        value={b.status}
                        onChange={(e) => setStatus(b.id, e.target.value)}
                        className={`rounded-full border px-2 py-1 text-[11px] font-semibold capitalize ${BADGE[b.status]}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {noteView && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center jgy-overlay-scrim p-4 backdrop-blur-sm"
          onClick={() => setNoteView(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-card p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-border pb-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Booking Notes
                </div>
                <div className="font-serif text-lg font-bold">
                  {noteView.member_name || "Devotee"}
                </div>
                {noteView.member_phone && (
                  <div className="text-xs text-primary">📞 {noteView.member_phone}</div>
                )}
              </div>
              <button onClick={() => setNoteView(null)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Date</span>
                <span>
                  {fmtDateOnly(noteView.booking_date)} · {noteView.booking_time?.slice(0, 5)}
                </span>
              </div>
              {noteView.visitor_names && (
                <div className="rounded-xl border border-border bg-muted/40 p-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                    Visitors
                  </div>
                  <pre className="whitespace-pre-wrap text-xs">{noteView.visitor_names}</pre>
                </div>
              )}
              <div className="rounded-xl border border-border bg-muted/40 p-3">
                <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                  Remarks
                </div>
                <p className="text-xs">{noteView.notes}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: any;
  tone: "saffron" | "cream" | "muted" | "gold";
  active: boolean;
  onClick: () => void;
}) {
  const map = {
    saffron: "from-primary/15 to-primary/5 text-primary",
    cream: "from-cream-deep to-secondary text-foreground",
    muted: "from-muted to-secondary text-muted-foreground",
    gold: "from-gold/25 to-primary/8 text-gold-foreground",
  }[tone];
  return (
    <button
      onClick={onClick}
      className={`group rounded-2xl border bg-gradient-to-br p-4 text-left shadow-sm transition hover:shadow-md ${map} ${active ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
    >
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5 opacity-80" />
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
          {active ? "Active" : ""}
        </span>
      </div>
      <div className="mt-3 font-serif text-3xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider opacity-80">
        {label}
      </div>
    </button>
  );
}
